import { classes } from "utils/classes";

import { formatCountdown } from "../utils";
import styles from "./ScheduleTime.scss";

const createFormatter = (timeZone?: string) => {
  const time = new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  });
  const day = new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone,
  });
  const zone = new Intl.DateTimeFormat(undefined, {
    timeZoneName: "short",
    timeZone,
  });

  return (date: Date) => ({
    time: time.format(date),
    day: day.format(date),
    zone:
      zone.formatToParts(date).find((p) => p.type === "timeZoneName")?.value ??
      "",
  });
};

type DateParts = ReturnType<ReturnType<typeof createFormatter>>;

const formatLocal = createFormatter();
const formatUtc = createFormatter("UTC");

function Row({ parts, secondary }: { parts: DateParts; secondary?: boolean }) {
  return (
    <>
      <span
        className={classes(styles.time, {
          [styles.secondary]: Boolean(secondary),
        })}
      >
        {parts.time}
      </span>
      <span
        className={classes(styles.day, {
          [styles.secondary]: Boolean(secondary),
        })}
      >
        {parts.day}
      </span>
      <span
        className={classes(styles.zone, {
          [styles.secondary]: Boolean(secondary),
        })}
      >
        {parts.zone}
      </span>
    </>
  );
}

interface ScheduleTimeProps {
  date: Date;
  now?: Date;
  label?: string;
  align?: "start" | "end";
  size?: "small" | "medium" | "large";
  className?: string;
  dataTestId?: string;
}

export function ScheduleTime({
  date,
  now,
  label,
  align = "start",
  size = "medium",
  className = "",
  dataTestId = "schedule-time",
}: ScheduleTimeProps) {
  return (
    <div
      className={classes(
        styles.container,
        styles[align],
        styles[size],
        className
      )}
      data-test-id={dataTestId}
    >
      {label && <span className={styles.label}>{label}</span>}
      <div className={styles.content}>
        <div className={styles.rows}>
          <Row parts={formatLocal(date)} />
          <Row parts={formatUtc(date)} secondary />
        </div>
        {now && (
          <span className={styles.countdown}>{formatCountdown(date, now)}</span>
        )}
      </div>
    </div>
  );
}
